import React, { useState } from 'react';
import { Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { formatDate } from '../utils/format';
import { serverMessage } from './PageState';

const ACTIONS = {
  sent: { buttonLabel: 'Log invoice sent', title: 'Record invoice sent', noteLabel: 'Invoice sent' },
  chased: { buttonLabel: 'Log invoice chased', title: 'Record invoice chased', noteLabel: 'Invoice chased' },
};

const EMPTY_FORM = { dateDue: '', sentTo: '', invoiceCode: '' };

/**
 * "Log invoice sent" / "Log invoice chased" buttons. Each opens a dialog capturing the invoice
 * details, which are recorded as a comment on the company via onAddComment(commentText).
 *
 * compact renders small buttons with no wrapper, for a card header such as the Comments card's
 * headerActions; otherwise they sit in their own row below the billing panel.
 */
const InvoiceActions = ({ onAddComment, canEdit, compact = false }) => {
  const [action, setAction] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!canEdit) {
    return null;
  }

  const openDialog = (actionKey) => {
    setForm(EMPTY_FORM);
    setError(null);
    setAction(actionKey);
  };

  const closeDialog = () => {
    if (!isSubmitting) {
      setAction(null);
    }
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const hasDetails = form.dateDue || form.sentTo.trim() || form.invoiceCode.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasDetails || isSubmitting) return;
    const parts = [];
    if (form.invoiceCode.trim()) parts.push(`Invoice code: ${form.invoiceCode.trim()}`);
    if (form.sentTo.trim()) parts.push(`Sent to: ${form.sentTo.trim()}`);
    // A date input gives a plain date, read as local midnight so it never shifts a day
    if (form.dateDue) parts.push(`Date due: ${formatDate(`${form.dateDue}T00:00:00`, form.dateDue)}`);
    const note = `${ACTIONS[action].noteLabel} - ${parts.join('; ')}`;

    try {
      setIsSubmitting(true);
      setError(null);
      await onAddComment(note);
      setAction(null);
    } catch (err) {
      setError(serverMessage(err, err.message || 'Could not record the invoice details'));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttons = (
    <>
      <Button variant={compact ? 'outline-secondary' : 'outline-primary'} size={compact ? 'sm' : undefined} onClick={() => openDialog('sent')}>
        {ACTIONS.sent.buttonLabel}
      </Button>
      <Button variant={compact ? 'outline-secondary' : 'outline-primary'} size={compact ? 'sm' : undefined} onClick={() => openDialog('chased')}>
        {ACTIONS.chased.buttonLabel}
      </Button>
    </>
  );

  return (
    <>
      {compact ? buttons : <div className="d-flex gap-2 mt-3">{buttons}</div>}

      <Modal show={action !== null} onHide={closeDialog} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton={!isSubmitting}>
            <Modal.Title as="h2" className="h5 mb-0">{action ? ACTIONS[action].title : ''}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Form.Group className="mb-3" controlId="invoiceActionCode">
              <Form.Label>Invoice code</Form.Label>
              <Form.Control
                type="text"
                value={form.invoiceCode}
                onChange={(e) => setField('invoiceCode', e.target.value)}
                placeholder="e.g. INV-1234"
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="invoiceActionSentTo">
              <Form.Label>Sent to</Form.Label>
              <Form.Control
                type="text"
                value={form.sentTo}
                onChange={(e) => setField('sentTo', e.target.value)}
                placeholder="e.g. name or email address"
              />
            </Form.Group>
            {action === 'sent' && (
              <Form.Group className="mb-3" controlId="invoiceActionDateDue">
                <Form.Label>Date due</Form.Label>
                <Form.Control
                  type="date"
                  value={form.dateDue}
                  onChange={(e) => setField('dateDue', e.target.value)}
                />
              </Form.Group>
            )}
            <Form.Text className="text-muted">Fill in at least one detail. It is added to the comments as a note.</Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeDialog} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || !hasDetails}>
              {isSubmitting && <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />}
              {isSubmitting ? 'Recording...' : 'Record'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceActions;
